import {
  SensitiveDataByRegion,
  VisibilityDataApiGetSensitiveDataByRegionRequest,
} from '@ariksa/data-scanning';
import {
  DataClassifiers,
  DataFootprintAnalyzed,
  DataSecurityIdentityPage,
  DataSourcesWithData,
  DataTypesApiSyncLabelsRequest,
  PageDocumentClassificationAggregateInfo,
  PageDocumentClassificationInfo,
  PageResourcesRead,
  PageSensitiveDataInfo,
  PageSourceBySensitiveData,
  ResourceDistributionCount,
  ResourceGroup,
  ResourcesApiGetDataTypeSensitiveSourcesRequest,
  ResourcesApiGetResourceGroupsRequest,
  ResourcesApiGetResourcesByGroupRequest,
  ResourcesApiGetResourcesCategoriesCountRequest,
  ResourcesApiGetSensitiveDataSourcesCountRequest,
  ResourcesApiGetSourcesDistributionCountRequest,
  SensitiveDataApiGetFilesBySensitiveDataRequest,
  SensitiveDataApiGetSensitiveDataLabelsCountRequest,
  SensitiveDataByAccount,
  VisibilityDataApiGetDataSourcesWithSensitiveDataRequest,
  VisibilityDataApiGetDocumentClassificationBySubtypeAndSourceRequest,
  VisibilityDataApiGetDocumentClassificationBySubtypesRequest,
  VisibilityDataApiGetDocumentClassificationByTypeAndSourceRequest,
  VisibilityDataApiGetDocumentClassificationByTypesRequest,
  VisibilityDataApiGetSensitiveDataByAccountRequest,
  VisibilityDataApiGetSensitiveDataInfoTableRequest,
} from '@ariksa/data-scanning/api';
import {
  CloudInsightApiGetRuntimeInsightsForDataSourcesRequest,
  ContextApiGetRiskContextResDataScanRequest,
  DataSourcesSummary,
  InsightApiGetDataSecurityRequest,
  PublicResourceSummary,
  ResourceApiGetDataSourcesSummaryRequest,
  ResourceSummaryApiGetPublicExposedResSummaryRequest,
  RiskContextResponse,
  RuntimeInsightBySources,
} from '@ariksa/inventory-core/api';
import { AlertsApiGetAlertingResourcesCountRequest } from '@ariksa/notification/api';
import { PayloadAction } from '@reduxjs/toolkit';
import { QueryAction } from 'services/types';
import { PagedQueryState } from 'services/utils/PagedQueryState';
import { QueryState } from 'services/utils/QueryState';

import { PageInfo } from 'components/Navigation';
import { createSlice } from 'utils/@reduxjs/toolkit';

import { ContainerState } from './types';

// The initial state of the Tag container
export const initialState: ContainerState = {
  selectedTab: 'DataSources',
  dataSources: PagedQueryState.init([]),
  dataSecurityIdentities: PagedQueryState.init([]),
  allDataResourceTypes: PagedQueryState.init([]),
  dataTypeSensitiveSources: PagedQueryState.init([]),
  sensitiveSourcesByDataType: PagedQueryState.init([]),
  filesByDataType: PagedQueryState.init([]),
  dataSourcesPieChart: QueryState.init({
    service_sources: [],
    total: 0,
  }),
  dataTypesRiskContext: QueryState.init({}),
  riskContext: QueryState.init({}),
  highRiskDataSources: QueryState.init([]),
  dataClassifiers: QueryState.init([]),
  resourceCount: QueryState.init({}),
  sensitiveDataByRegion: QueryState.init({ regions: [], total: 0 }),
  dataSourcesByRegion: QueryState.init({ regions: [], total: 0 }),
  sourcesByAccount: QueryState.init({} as SensitiveDataByAccount),
  sensitiveDataByAgnosticSources: QueryState.init(
    {} as ResourceDistributionCount,
  ),

  analyzedSources: QueryState.init({}),
  sensitiveDataSourceCount: QueryState.init(0),
  labelCount: QueryState.init({}),
  publiclyExposedDataSources: QueryState.init([]),
  summaryOfAllDataSources: QueryState.init({} as DataSourcesSummary),
  alertingCount: QueryState.init(0),
  documentSubTypes: PagedQueryState.init([]),
  documentSubTypeDataSources: PagedQueryState.init([]),
  dataSourcesByType: PagedQueryState.init([]),
  documentTypeDetails: QueryState.init({}),
  runtimeInsights: QueryState.init([]),
  syncLabels: QueryState.init({}),
};

const dataDashboardSlice = createSlice({
  name: 'dataDashboard',
  initialState,
  reducers: {
    getDataSecurityIdentities(
      state,
      action: QueryAction<
        DataSecurityIdentityPage,
        InsightApiGetDataSecurityRequest
      >,
    ) {
      state.dataSecurityIdentities = PagedQueryState.next(
        state.dataSecurityIdentities,
        action,
        {
          mapData: response => response.items ?? [],
          mapTotalCount: response => response.total ?? 0,
        },
      );
    },

    updateDataSecurityIdentitiesPageInfo(
      state,
      action: PayloadAction<PageInfo>,
    ) {
      state.dataSecurityIdentities.page.info = action.payload;
    },

    getDataSources(
      state,
      action: QueryAction<
        PageResourcesRead,
        ResourcesApiGetDataTypeSensitiveSourcesRequest
      >,
    ) {
      state.dataSources = PagedQueryState.next(state.dataSources, action, {
        mapData: r => r.items,
        mapTotalCount: r => r.total ?? 0,
      });
    },

    getAllDataResourceTypes(
      state,
      action: QueryAction<
        ResourceGroup[],
        ResourcesApiGetResourceGroupsRequest
      >,
    ) {
      state.allDataResourceTypes = QueryState.next(
        state.allDataResourceTypes,
        action,
      );
    },

    getDataTypes(
      state,
      action: QueryAction<
        PageSensitiveDataInfo,
        VisibilityDataApiGetSensitiveDataInfoTableRequest
      >,
    ) {
      state.dataSources = PagedQueryState.next(state.dataSources, action, {
        mapData: r => r.items,
        mapTotalCount: r => r.total ?? 0,
      });
    },

    getDataClassifiers(state, action: QueryAction<DataClassifiers[]>) {
      state.dataClassifiers = QueryState.next(state.dataClassifiers, action);
    },

    getDocumentTypes(
      state,
      action: QueryAction<
        PageDocumentClassificationAggregateInfo,
        VisibilityDataApiGetDocumentClassificationByTypesRequest
      >,
    ) {
      state.dataSources = PagedQueryState.next(state.dataSources, action, {
        mapData: r => r.items,
        mapTotalCount: r => r.total ?? 0,
      });
    },

    getDocumentSubTypes(
      state,
      action: QueryAction<
        PageDocumentClassificationAggregateInfo,
        VisibilityDataApiGetDocumentClassificationBySubtypesRequest
      >,
    ) {
      state.documentSubTypes = PagedQueryState.next(
        state.documentSubTypes,
        action,
        {
          mapData: r => r.items,
          mapTotalCount: r => r.total ?? 0,
        },
      );
    },

    getDataSourcesByDocumentSubType(
      state,
      action: QueryAction<
        PageDocumentClassificationInfo,
        VisibilityDataApiGetDocumentClassificationBySubtypeAndSourceRequest
      >,
    ) {
      state.dataSourcesByType = PagedQueryState.next(
        state.dataSourcesByType,
        action,
        {
          mapData: r => r.items,
          mapTotalCount: r => r.total ?? 0,
        },
      );
    },
    getDataSourcesByDocumentType(
      state,
      action: QueryAction<
        PageDocumentClassificationInfo,
        VisibilityDataApiGetDocumentClassificationByTypeAndSourceRequest
      >,
    ) {
      state.dataSourcesByType = PagedQueryState.next(
        state.dataSourcesByType,
        action,
        {
          mapData: r => r.items,
          mapTotalCount: r => r.total ?? 0,
        },
      );
    },
    getDataSourcesByResourceType(
      state,
      action: QueryAction<
        PageResourcesRead,
        ResourcesApiGetResourcesByGroupRequest & {
          readonly searchTerm?: string | null;
        }
      >,
    ) {
      state.dataSourcesByType = PagedQueryState.next(
        state.dataSourcesByType,
        action,
        {
          mapData: r => r.items,
          mapTotalCount: r => r.total ?? 0,
        },
      );
    },

    updateDataTablePageInfo(state, action: PayloadAction<PageInfo>) {
      state.dataSources.page.info = action.payload;
    },
    updateDataTypeSourcesPageInfo(state, action: PayloadAction<PageInfo>) {
      state.dataTypeSensitiveSources.page.info = action.payload;
    },
    // Keep drawer pagination independent from the data-type sources view state.
    updateSensitiveSourcesByDataTypePageInfo(
      state,
      action: PayloadAction<PageInfo>,
    ) {
      state.sensitiveSourcesByDataType.page.info = action.payload;
    },
    updateDataSourcesByTypePageInfo(state, action: PayloadAction<PageInfo>) {
      state.dataSourcesByType.page.info = action.payload;
    },
    updateFilesByDataTypePageInfo(state, action: PayloadAction<PageInfo>) {
      state.filesByDataType.page.info = action.payload;
    },

    getDataSourcesPieChartData(
      state,
      action: QueryAction<
        DataSourcesWithData,
        VisibilityDataApiGetDataSourcesWithSensitiveDataRequest
      >,
    ) {
      state.dataSourcesPieChart = QueryState.next(
        state.dataSourcesPieChart,
        action,
      );
    },

    getRiskContextForDataTypes(
      state,
      action: QueryAction<
        Record<string, RiskContextResponse[]>,
        ContextApiGetRiskContextResDataScanRequest
      >,
    ) {
      state.dataTypesRiskContext = QueryState.next(
        state.dataTypesRiskContext,
        action,
      );
    },

    getResourceCount(state, action: QueryAction<any, any>) {
      state.resourceCount = QueryState.next(state.resourceCount, action);
    },

    getSensitiveDataByRegion(
      state,
      action: QueryAction<
        SensitiveDataByRegion,
        VisibilityDataApiGetSensitiveDataByRegionRequest
      >,
    ) {
      state.sensitiveDataByRegion = QueryState.next(
        state.sensitiveDataByRegion,
        action,
      );
    },

    getDataSourcesByRegion(
      state,
      action: QueryAction<
        SensitiveDataByRegion,
        VisibilityDataApiGetSensitiveDataByRegionRequest
      >,
    ) {
      state.dataSourcesByRegion = QueryState.next(
        state.dataSourcesByRegion,
        action,
      );
    },

    getSourcesByAccounts(
      state,
      action: QueryAction<
        SensitiveDataByAccount,
        VisibilityDataApiGetSensitiveDataByAccountRequest
      >,
    ) {
      state.sourcesByAccount = QueryState.next(state.sourcesByAccount, action);
    },

    updateSelectedTab(state, action: PayloadAction<string>) {
      state.selectedTab = action.payload;
    },

    getAnalyzedSources(
      state,
      action: QueryAction<
        DataFootprintAnalyzed,
        ResourcesApiGetResourcesCategoriesCountRequest
      >,
    ) {
      state.analyzedSources = QueryState.next(state.analyzedSources, action);
    },

    getSensitiveDataSourceCount(
      state,
      action: QueryAction<
        number,
        ResourcesApiGetSensitiveDataSourcesCountRequest
      >,
    ) {
      state.sensitiveDataSourceCount = QueryState.next(
        state.sensitiveDataSourceCount,
        action,
      );
    },

    getSensitiveDataLabelCount(
      state,
      action: QueryAction<
        Record<string, number>,
        SensitiveDataApiGetSensitiveDataLabelsCountRequest
      >,
    ) {
      state.labelCount = QueryState.next(state.labelCount, action);
    },

    // Fetch sensitive-record totals independently for each identity resource.
    getSensitiveRecordsForResource(
      _state,
      _action: QueryAction<
        Record<string, number>,
        SensitiveDataApiGetSensitiveDataLabelsCountRequest
      >,
    ) {},

    getPubliclyExposedDataSources(
      state,
      action: QueryAction<
        PublicResourceSummary[],
        ResourceSummaryApiGetPublicExposedResSummaryRequest
      >,
    ) {
      state.publiclyExposedDataSources = QueryState.next(
        state.publiclyExposedDataSources,
        action,
      );
    },

    getSummaryOfAllDataSources(
      state,
      action: QueryAction<
        DataSourcesSummary,
        ResourceApiGetDataSourcesSummaryRequest
      >,
    ) {
      state.summaryOfAllDataSources = QueryState.next(
        state.summaryOfAllDataSources,
        action,
      );
    },

    getFilesByDataType(
      state,
      action: QueryAction<
        PageSourceBySensitiveData,
        SensitiveDataApiGetFilesBySensitiveDataRequest
      >,
    ) {
      state.filesByDataType = PagedQueryState.next(
        state.filesByDataType,
        action,
        { mapData: r => r.items, mapTotalCount: r => r.total ?? 0 },
      );
    },

    getDataTypeSources(
      state,
      action: QueryAction<
        PageResourcesRead,
        ResourcesApiGetDataTypeSensitiveSourcesRequest
      >,
    ) {
      state.dataTypeSensitiveSources = PagedQueryState.next(
        state.dataTypeSensitiveSources,
        action,
        { mapData: r => r.items, mapTotalCount: r => r.total ?? 0 },
      );
    },

    getSensitiveSourcesByDataType(
      state,
      action: QueryAction<
        PageResourcesRead,
        ResourcesApiGetDataTypeSensitiveSourcesRequest
      >,
    ) {
      // Route drawer responses into their own paged table state.
      state.sensitiveSourcesByDataType = PagedQueryState.next(
        state.sensitiveSourcesByDataType,
        action,
        { mapData: r => r.items, mapTotalCount: r => r.total ?? 0 },
      );
    },

    getAlertingCount(
      state,
      action: QueryAction<number, AlertsApiGetAlertingResourcesCountRequest>,
    ) {
      state.alertingCount = QueryState.next(state.alertingCount, action);
    },

    updateDocumentSubTypesPagination(state, action: PayloadAction<PageInfo>) {
      state.documentSubTypes.page.info = action.payload;
    },

    getSensitiveDataByAgnosticSources(
      state,
      action: QueryAction<
        ResourceDistributionCount,
        ResourcesApiGetSourcesDistributionCountRequest
      >,
    ) {
      state.sensitiveDataByAgnosticSources = QueryState.next(
        state.sensitiveDataByAgnosticSources,
        action,
      );
    },

    getHighRiskDataSources(
      state,
      action: QueryAction<
        PageResourcesRead,
        ResourcesApiGetDataTypeSensitiveSourcesRequest
      >,
    ) {
      state.highRiskDataSources = QueryState.next(
        state.highRiskDataSources,
        action,
        { mapData: r => r.items },
      );
    },

    getRuntimeInsights(
      state,
      action: QueryAction<
        RuntimeInsightBySources[],
        CloudInsightApiGetRuntimeInsightsForDataSourcesRequest
      >,
    ) {
      state.runtimeInsights = QueryState.next(state.runtimeInsights, action);
    },

    syncLabels(state, action: QueryAction<any, DataTypesApiSyncLabelsRequest>) {
      state.syncLabels = QueryState.next(state.syncLabels, action);
    },
  },
});

export const { actions, reducer, name: sliceKey } = dataDashboardSlice;
